<template>
  <Form
    size="small"
    :layout="inline ? 'inline' : 'horizontal'"
    :colon="colon"
    label-align="left"
    :label-col="{ span: labelCol }"
    class="p-form"
    :class="{ disabled }"
  >
    <template v-for="f in formItems" :key="f.key">
      <FormItem
        :label="f.label"
        :required="f.required"
        @keydown.capture="e => handleFormKeydown(e, f)"
      >
        <Checkbox
          v-if="f.type === 'Checkbox'"
          v-model:checked="fd[f.key]"
          :disabled="disabled || f.disabled"
          @change="handleCheckbox(f.key)"
        ></Checkbox>
        <div
          v-else-if="singleEditKey !== f.key || f.type === 'Text'"
          class="single-edit-trigger"
          :class="{ 'is-text': f.type === 'Text' && !$slots[f.key] }"
          @click="handleClick(f)"
        >
          <div>{{ f.info ?? fd[f.displayKey ?? f.key] ?? '-' }}</div>
          <div class="single-suffix" v-if="$slots[f.key]">
            <slot :name="f.key"></slot>
          </div>
        </div>
        <!-- <div v-else-if="f.type === 'Text'" class="w-full">
          <Input
            style="padding: 0 7px"
            size="small"
            :placeholder="getPlaceholder(f)"
            readonly
            class="small-input flex-1"
            :value="fd[f.key]"
          >
            <template #suffix v-if="$slots[f.key]">
              <slot :name="f.key"></slot>
            </template>
          </Input>
        </div> -->
        <RadioGroup
          v-else-if="f.type === 'RadioGroup'"
          v-model:value="fd[f.key]"
          :disabled="f.disabled"
          button-style="solid"
          size="small"
          style="margin-top: 0; min-height: auto"
        >
          <RadioButton v-for="opt in f.options as any[]" :key="opt.value" :value="opt.value">{{
            opt.label
          }}</RadioButton>
        </RadioGroup>
        <component
          v-else
          ref="comp"
          :is="getComponent(f.type)"
          :placeholder="getPlaceholder(f)"
          size="small"
          allow-search
          :field-names="{ label: 'name', value: 'id' }"
          v-bind="{ ...f }"
          v-model:value="fd[f.key]"
          class="small-input w-full"
          @blur="handleBlur(f.key)"
        >
          <template v-if="$slots[f.key]" #suffix>
            <slot :name="f.key"></slot>
          </template>
        </component>
        <!-- <div style="margin-left: 8px" v-if="$slots[f.key]">
        </div> -->
      </FormItem>
      <div class="mb-4 form-item-bottom" v-if="$slots[`bottom${f.key}`]">
        <slot :name="`bottom${f.key}`"></slot>
      </div>
    </template>
  </Form>
</template>

<script lang="ts" setup>
import { watch, ref, nextTick } from 'vue'
import {
  Form,
  FormItem,
  Select,
  Input,
  InputNumber,
  Checkbox,
  RadioGroup,
  RadioButton,
  DatePicker,
} from 'ant-design-vue'
import { cloneDeep, debounce, isEqual } from 'lodash-es'

export interface PFormItemProps {
  key: string
  displayKey?: string
  info?: string
  label: string
  type: 'Text' | 'Input' | 'Select' | 'InputNumber' | 'Checkbox' | 'RadioGroup' | 'DatePicker'
  options?: unknown[]
  placeholder?: string
  required?: boolean
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    formItems: PFormItemProps[]
    formData?: Record<string, any>
    inline?: boolean
    colon?: boolean
    labelCol?: number
    disabled?: boolean
  }>(),
  {
    inline: false,
    colon: false,
    labelCol: 4,
    formData: () => ({}),
    disabled: false,
  },
)

const emit = defineEmits(['change', 'click'])

const fd = ref(cloneDeep(props.formData))
const singleEditKey = ref<string>()

const comp = ref()
watch(
  () => props.formData,
  pfd => {
    fd.value = cloneDeep(pfd)
  },
  { deep: true },
)

const typeMap: Record<PFormItemProps['type'], any> = {
  Text: 'div',
  Input,
  Select,
  InputNumber,
  Checkbox,
  RadioGroup,
  DatePicker,
}

function getComponent(type: PFormItemProps['type']) {
  return typeMap[type] ?? 'div'
}

const placeholderMap: Record<PFormItemProps['type'], string> = {
  Text: '',
  Input: '请输入',
  InputNumber: '请输入',
  Select: '请选择',
  Checkbox: '',
  RadioGroup: '',
  DatePicker: '请选择',
}

function getPlaceholder(item: PFormItemProps) {
  return item.placeholder ?? `${placeholderMap[item.type]}${item.label}`
}

function handleClick(f: PFormItemProps) {
  if (props.disabled || f.type === 'Text') {
    emit('click', f.key)
    return
  }
  singleEditKey.value = f.key
  nextTick(() => {
    comp.value?.[0]?.focus?.()
  })
}

function handleCheckbox(key: string) {
  if (Object.is(props.formData[key], fd.value[key])) return
  emit('change', key, fd.value[key])
}

function handleBlur(key: string) {
  if (!singleEditKey.value) return
  singleEditKey.value = undefined
  if (Object.is(props.formData[key], fd.value[key])) return
  emit('change', key, fd.value[key])
}

function stopWithoutSave(e: KeyboardEvent) {
  if (e.ctrlKey && e.key === 's') return
  if (e.key === 'Escape') return
  e.stopPropagation()
}

function handleFormKeydown(e: KeyboardEvent, formItem: PFormItemProps) {
  stopWithoutSave(e)
  if (e.key !== 'Enter') return
  if (formItem.type !== 'Input') return
  singleEditKey.value = undefined
  const key = formItem.key
  if (!Object.is(props.formData[key], fd.value[key])) {
    emit('change', key, fd.value[key])
  }
  const index = props.formItems.findIndex(f => f.key === formItem.key)
  const next = props.formItems[index + 1]
  if (next?.type === 'Input') {
    nextTick(() => {
      handleClick(next)
    })
  }
}

defineExpose({
  editByKey: (key: string) => {
    singleEditKey.value = key
    nextTick(() => {
      comp.value?.[0]?.focus?.()
      comp.value?.[0]?.select?.()
    })
  },
})
</script>

<style lang="less" scoped>
.p-form {
  .ant-form-item {
    flex: 0 0 23%;
  }

  :deep(.ant-form-item-control-input-content) {
    display: flex;
    align-items: center;
    // overflow: hidden;
  }

  :deep(.multi-select-suffix) {
    width: 22px !important;
    height: 22px !important;
    margin: 0 3px 0 -25px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .single-edit-trigger {
    width: 100%;
    height: 26px;
    line-height: 26px;
    display: flex;
    justify-content: space-between;

    .single-suffix {
      display: none;
    }

    &:not(.is-text):hover {
      margin-left: -4px;
      padding: 0 4px;
      outline: 1px solid rgba(0, 0, 0, 0.15);
      .single-suffix {
        display: block;
      }
    }
  }

  &.disabled {
    .single-edit-trigger {
      &:hover {
        outline: none;
        .single-suffix {
          display: none;
        }
      }
    }
  }
}
</style>
